import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  X,
  LayoutDashboard,
  Users,
  UsersRound,
  FolderKanban,
  Key,
  Route,
  FileText,
  Settings,
  BookOpen,
  HelpCircle,
  Bell,
  Building2,
  Server,
  ChevronDown,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { Permission } from "@/types";

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  gradient: string;
  requiredPermission?: Permission;
  requireSuperAdmin?: boolean;
  requireOrgAdmin?: boolean;
}

interface NavigationSection {
  name: string;
  icon: any;
  items: NavigationItem[];
}

const navigationSections: (NavigationItem | NavigationSection)[] = [
  {
    name: "Dashboard",
    href: "/admin",
    icon: LayoutDashboard,
    gradient: "from-blue-400 to-blue-600",
  },
  {
    name: "Tenant Management",
    icon: Building2,
    items: [
      {
        name: "Users",
        href: "/admin/users",
        icon: Users,
        gradient: "from-green-400 to-green-600",
        requiredPermission: Permission.READ_USERS,
      },
      {
        name: "Teams",
        href: "/admin/teams",
        icon: UsersRound,
        gradient: "from-purple-400 to-purple-600",
        requiredPermission: Permission.READ_TEAM,
      },
      {
        name: "Projects",
        href: "/admin/projects",
        icon: FolderKanban,
        gradient: "from-orange-400 to-orange-600",
        requiredPermission: Permission.READ_PROJECT,
      },
      {
        name: "API Keys",
        href: "/admin/api-keys",
        icon: Key,
        gradient: "from-red-400 to-red-600",
        requiredPermission: Permission.READ_API_KEYS,
      },
    ],
  },
  {
    name: "System Management",
    icon: Server,
    items: [
      {
        name: "Routes",
        href: "/admin/routes",
        icon: Route,
        gradient: "from-teal-400 to-teal-600",
        requiredPermission: Permission.READ_ROUTES,
      },
      {
        name: "Policies",
        href: "/admin/policies",
        icon: FileText,
        gradient: "from-indigo-400 to-indigo-600",
        requiredPermission: Permission.READ_POLICIES,
      },
      {
        name: "Notifications",
        href: "/admin/notifications",
        icon: Bell,
        gradient: "from-yellow-400 to-orange-600",
        requireOrgAdmin: true,
      },
      {
        name: "SSO Origins",
        href: "/admin/sso-origins",
        icon: Shield,
        gradient: "from-pink-400 to-purple-600",
        requireOrgAdmin: true,
      },
      {
        name: "Settings",
        href: "/admin/settings",
        icon: Settings,
        gradient: "from-gray-400 to-gray-600",
      },
    ],
  },
];

const SectionHeader = ({
  section,
  isExpanded,
  onToggle,
}: {
  section: NavigationSection;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const Icon = section.icon;

  return (
    <button
      onClick={onToggle}
      className="group flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-all duration-200 hover:bg-white/5 hover:text-gray-300"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {section.name}
        </span>
      </div>
      <ChevronDown
        className={cn(
          "h-3 w-3 transition-transform duration-200",
          isExpanded ? "rotate-180" : "",
        )}
      />
    </button>
  );
};

export default function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const { hasPermission, isSuperAdmin, isOrgAdmin } = useAuthStore();

  // State for expand/collapse sections
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    const saved = localStorage.getItem("sidebar-expanded-sections");
    return saved
      ? JSON.parse(saved)
      : { "Tenant Management": true, "System Management": true };
  });

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const newState = { ...prev, [sectionName]: !prev[sectionName] };
      localStorage.setItem(
        "sidebar-expanded-sections",
        JSON.stringify(newState),
      );
      return newState;
    });
  };

  // Filter items based on permissions
  const filterItem = (item: NavigationItem) => {
    if (isSuperAdmin()) return true;
    if (item.requireSuperAdmin) return false;
    if (item.requireOrgAdmin && !isOrgAdmin()) return false;
    if (!item.requiredPermission) return true;
    return hasPermission(item.requiredPermission);
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header with logo */}
      <div className="relative px-6 py-6">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />

        <div className="relative flex items-center space-x-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-400 to-purple-600 p-2 shadow-lg">
            <div className="h-full w-full rounded-lg bg-white/20" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Atlas Guard</h1>
            <p className="text-xs text-gray-300">Admin Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-4 py-4">
        {navigationSections.map((item) => {
          // Check if it's a section or a standalone item
          if ("items" in item) {
            // It's a section
            const section = item as NavigationSection;
            const isExpanded = expandedSections[section.name];

            // Filter visible items based on permissions
            const visibleItems = section.items.filter(filterItem);

            // Don't show section if no items are visible
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.name} className="space-y-1">
                <SectionHeader
                  section={section}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSection(section.name)}
                />

                {/* Collapsible items */}
                {isExpanded && (
                  <div className="ml-4 space-y-1 border-l border-white/10 pl-2">
                    {visibleItems.map((subItem) => {
                      const isActive = location.pathname === subItem.href;
                      const Icon = subItem.icon;

                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          onClick={onClose}
                          className={cn(
                            "group relative flex items-center rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200",
                            isActive
                              ? "bg-white/10 text-white shadow-lg"
                              : "text-gray-300 hover:bg-white/5 hover:text-white",
                          )}
                        >
                          {isActive && (
                            <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-blue-400 to-purple-600" />
                          )}

                          <div
                            className={cn(
                              "mr-3 rounded-lg p-1.5 transition-all",
                              isActive
                                ? cn(
                                    "bg-gradient-to-br",
                                    subItem.gradient,
                                    "shadow-lg",
                                  )
                                : "bg-white/10 group-hover:bg-white/20",
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 text-white" />
                          </div>

                          <span className="text-sm">{subItem.name}</span>

                          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/5 to-purple-600/0 opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          } else {
            // It's a standalone item (Dashboard)
            const navItem = item as NavigationItem;

            // Check permissions
            if (!filterItem(navItem)) return null;

            const isActive = location.pathname === navItem.href;
            const Icon = navItem.icon;

            return (
              <Link
                key={navItem.name}
                to={navItem.href}
                onClick={onClose}
                className={cn(
                  "group relative flex items-center rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-white/10 text-white shadow-lg"
                    : "text-gray-300 hover:bg-white/5 hover:text-white",
                )}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-blue-400 to-purple-600" />
                )}

                <div
                  className={cn(
                    "mr-3 rounded-lg p-2 transition-all",
                    isActive
                      ? cn("bg-gradient-to-br", navItem.gradient, "shadow-lg")
                      : "bg-white/10 group-hover:bg-white/20",
                  )}
                >
                  <Icon className="h-4 w-4 text-white" />
                </div>

                <span className="font-medium">{navItem.name}</span>

                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/5 to-purple-600/0 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            );
          }
        })}
      </nav>

      {/* Help section */}
      <div className="mx-4 mb-4 rounded-xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-4 backdrop-blur-sm">
        <div className="mb-3 flex items-center space-x-3">
          <div className="rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 p-2">
            <HelpCircle className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Need help?</h3>
            <p className="text-xs text-gray-300">Check our documentation</p>
          </div>
        </div>
        <button className="flex w-full items-center justify-center space-x-2 rounded-lg bg-white/10 py-2 text-xs font-medium text-white transition-colors hover:bg-white/20">
          <BookOpen className="h-3 w-3" />
          <span>Documentation</span>
        </button>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 px-4 py-4">
        <div className="text-center">
          <p className="text-xs font-medium text-gray-300">Atlas Guard Admin</p>
          <p className="text-xs text-gray-400">Version 1.0.0</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform shadow-2xl transition-transform duration-300 ease-in-out lg:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-lg bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
        <SidebarContent />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:overflow-y-auto lg:shadow-xl">
        <SidebarContent />
      </div>
    </>
  );
}
